import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import CustomSelect from "views/Components/CustomSelect";
import { ClipLoader } from "react-spinners";
import { TYPE_OPTIONS, LANGUAGE_OPTIONS } from "services/constants";
import localization from "services/localization";
import IconPicker from "views/Components/IconPicker";

const AddEditPromptCategoryModal = ({
  show,
  title,
  formData,
  errors,
  onChange,
  onSubmit,
  onClose,
  loading,
  modules = [],
  onFormChange,
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGE_OPTIONS[0]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [type, setType] = useState(null);

  const moduleOptions = useMemo(
    () =>
      modules.map((m) => ({
        value: m.id,
        label: m.name,
        type: m.type,
      })),
    [modules],
  );

  // Sync type + module when editing
  useEffect(() => {
    if (formData?.type) {
      const foundType = TYPE_OPTIONS.find((t) => t.value === formData.type);

      if (foundType?.value !== type?.value) {
        setType(foundType || null);
      }
    }

    if (formData?.module_id) {
      const foundModule = moduleOptions.find(
        (m) => m.value === formData.module_id,
      );

      if (foundModule?.value !== selectedModule?.value) {
        setSelectedModule(foundModule || null);
      }
    }
  }, [
    formData?.type,
    formData?.module_id,
    moduleOptions,
    type,
    selectedModule,
  ]);

  // Reset selected module if type changes
  useEffect(() => {
    if (type) {
      const validModule = moduleOptions.find(
        (m) => m.type === type.value && m.value === formData?.module_id,
      );

      // 🔥 IMPORTANT: Only update if different
      if (validModule?.value !== selectedModule?.value) {
        setSelectedModule(validModule || null);
      }
    }
  }, [type, formData?.module_id, moduleOptions, selectedModule]);

  // Handle language changes
  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang);
  };

  // Handle module select
  const handleModuleChange = (selectedOption) => {
    setSelectedModule(selectedOption);
    onChange({
      target: { name: "module_id", value: selectedOption?.value || "" },
    });
  };

  // Handle type
  const handleSelectChange = (field, selectedOption) => {
    setType(selectedOption);
    onChange({
      target: { name: field, value: selectedOption?.value || "" },
    });
  };

  const filteredModuleOptions = type
    ? moduleOptions.filter((m) => m.type === type.value)
    : moduleOptions;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      className="themed-modal addEditModal"
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={onSubmit}>
        <Modal.Body>
          {/* Language Selector */}
          <div className="mb-3">
            <Form.Label className="bootstrap-label">
              {localization.language || "Language"}
            </Form.Label>

            <CustomSelect
              options={LANGUAGE_OPTIONS}
              value={selectedLanguage}
              onChange={handleLanguageChange}
              placeholder={localization.select_language || "Select language"}
              className="w-40"
            />
          </div>

          <Row>
            {/* Title */}
            <Col md={12} className="mb-3">
              <Form.Group className="required">
                <Form.Label className="bootstrap-label">
                  {" "}
                  {localization.title || "Title"} ({selectedLanguage.value})
                </Form.Label>
                <Form.Control
                  type="text"
                  name={`title.${selectedLanguage.value}`}
                  value={formData?.title?.[selectedLanguage.value] || ""}
                  onChange={(e) =>
                    onChange({
                      target: {
                        name: "title",
                        value: {
                          ...formData.title,
                          [selectedLanguage.value]: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder={`${localization.enter_title || "Enter title"} (${
                    selectedLanguage.value
                  })`}
                  isInvalid={!!errors.title}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.title}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Description */}
            <Col md={12} className="mb-3">
              <Form.Group className="required">
                <Form.Label className="bootstrap-label">
                  {" "}
                  {localization.description || "Description"} (
                  {selectedLanguage.value})
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name={`description.${selectedLanguage.value}`}
                  value={formData?.description?.[selectedLanguage.value] || ""}
                  onChange={(e) =>
                    onChange({
                      target: {
                        name: "description",
                        value: {
                          ...formData.description,
                          [selectedLanguage.value]: e.target.value,
                        },
                      },
                    })
                  }
                  placeholder={`${
                    localization.enter_description || "Enter description"
                  } (${selectedLanguage.value})`}
                  isInvalid={!!errors.description}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.description}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Type */}
            <Col md={6} className="mb-3">
              <Form.Group className="required">
                <Form.Label className="bootstrap-label">
                  {localization.type || "Type"}
                </Form.Label>
                <CustomSelect
                  options={TYPE_OPTIONS}
                  value={type}
                  onChange={(val) => handleSelectChange("type", val)}
                  placeholder={localization.select_type || "Select type"}
                />
                {errors.type && (
                  <div className="text-danger small mt-1">{errors.type}</div>
                )}
              </Form.Group>
            </Col>

            {/* Module Selection */}
            <Col md={6} className="mb-3">
              <Form.Group className="required">
                <Form.Label className="bootstrap-label">
                  {localization.select_module || "Select Module"}
                </Form.Label>
                <CustomSelect
                  options={filteredModuleOptions}
                  value={selectedModule}
                  onChange={handleModuleChange}
                  placeholder={localization.choose_module || "Choose module"}
                />
                {errors.module_id && (
                  <div className="text-danger small mt-1">
                    {errors.module_id}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="bootstrap-label">
                  {localization.icon || "Icon"}
                </Form.Label>
                <IconPicker
                  value={formData.icon}
                  onChange={(value) => onFormChange("icon", value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button className="action-btn secondary" onClick={onClose}>
            {localization.cancel || "Cancel"}
          </Button>
          <Button
            className="action-btn primary no-border"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <ClipLoader color="var(--primary-icon)" size={20} />
            ) : (
              localization.save || "Save"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddEditPromptCategoryModal;
